/*
 * @Description: 将模板组件转换为函数式调用组件 (Vue 3 版本)
 */
import { createApp, h, type Component, type ComponentPublicInstance } from 'vue';
import ElementPlus from 'element-plus';
import { i18n } from '@/locales';
import { Icon } from '@/components/basic-components/icon';
import jaJP from 'element-plus/es/locale/lang/ja';
import { registerDirectives } from '@/directives';
export type RecordListener = Record<string, (...arg: any) => any>;
export type MountTemplate = Component;
export type MountConfig = {
  props?: Record<string, any>;
  parentEl?: HTMLElement | string;
  on?: RecordListener;
  nativeOn?: RecordListener;
  style?: Record<string, string>;
  rootProps?: Record<string, any>; // 如 store, router
};

export default function getCustomMountComponent(templateComponent: MountTemplate, initOptions?: MountConfig) {
  const {
    props: initProps = {},
    on: initOn = {},
    nativeOn: initNativeOn = {},
    style: initStyle,
    rootProps = {},
  } = initOptions || {};
  let parentEl = getMountEl(initOptions?.parentEl);

  return function insertComponent(options?: MountConfig): ComponentPublicInstance {
    const { props = {}, on = {}, nativeOn = {}, style, parentEl: currentParent } = options || {};
    const mountedEl = document.createElement('div');

    // 合并 props 和 on
    const mergedProps = { ...initProps, ...props };
    const mergedOn = { ...initOn, ...on };
    const mergedNativeOn = { ...initNativeOn, ...nativeOn };
    const mergedStyle = { ...initStyle, ...style };

    // 创建包装组件
    const WrapperComponent = {
      setup() {
        // 获取事件配置，添加 destroy 和 close 方法
        const getOnConfig = () => {
          const onFns = Object.keys(mergedOn).reduce((pre: Record<string, any>, key) => {
            // 转换为 Vue 3 h() 函数需要的格式：onEventName
            const eventKey = `on${key.charAt(0).toUpperCase()}${key.slice(1)}`;
            pre[eventKey] = mergedOn[key];
            return pre;
          }, {});

          return {
            ...onFns,
            destroy: deleteSelf,
            onClose: (...arg: any) => {
              if (mergedOn.close && typeof mergedOn.close === 'function') {
                mergedOn.close(...arg);
              }
              deleteSelf();
            },
          };
        };

        const deleteSelf = () => {
          setTimeout(() => {
            if (app) {
              app.unmount();
              app = null;
            }
          }, 0);
        };

        return () => h(
          templateComponent,
          {
            ...mergedProps,
            ...getOnConfig(),
            ...mergedNativeOn,
            style: mergedStyle,
          }
        );
      },
    };

    // 创建 app 实例
    let app = createApp(WrapperComponent, rootProps) as any;

    // 注册 Element Plus 和 i18n
    app.use(ElementPlus,{
      locale: jaJP,
    });
    app.use(i18n);
    app.component('Icon', Icon);
    // 注册全局指令
    registerDirectives(app);
    // 挂载
    const instance = app.mount(mountedEl);
    parentEl = getMountEl(currentParent || parentEl);
    parentEl.appendChild(mountedEl);

    // 在 unmount 时移除 DOM
    const originalUnmount = app.unmount.bind(app);
    app.unmount = () => {
      try {
        if (mountedEl && mountedEl.parentNode) {
          mountedEl.parentNode.removeChild(mountedEl);
        }
      } catch (e) {
        // ignore
      }
      originalUnmount();
    };

    // 添加 destroy 方法到 instance
    const deleteSelf = () => {
      setTimeout(() => {
        if (app) {
          app.unmount();
          app = null;
        }
      }, 0);
    };

    (instance as any).destroy = deleteSelf;

    return instance as ComponentPublicInstance;
  };
}

function getMountEl(value: HTMLElement | string | undefined): HTMLElement {
  if (!value) return document.body;
  if (value instanceof HTMLElement) return value;
  try {
    return document.querySelector(value) || document.body;
  } catch (e) {
    return document.body;
  }
}
